import { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { withTranslation, WithTranslation } from 'react-i18next';

interface Props extends WithTranslation {
  children: ReactNode;
  fallback?: ReactNode;
  /** Fill the content area instead of the viewport — for a boundary nested
   *  inside the app shell, where min-h-screen would push the layout. */
  contained?: boolean;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * Global Error Boundary - Catches unhandled React errors
 * and displays a user-friendly error page.
 */
class ErrorBoundaryClass extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.setState({ errorInfo });

    // Always log errors (production and development)
    console.error('ErrorBoundary caught an error:', error);
    console.error('Component stack:', errorInfo.componentStack);

    // Report to error monitoring if available (e.g., Sentry)
    if (typeof window !== 'undefined' && (window as unknown as Record<string, unknown>).Sentry) {
      try {
        const Sentry = (window as unknown as Record<string, unknown>).Sentry as {
          captureException: (err: Error, ctx?: unknown) => void;
        };
        Sentry.captureException(error, { extra: { componentStack: errorInfo.componentStack } });
      } catch {
        // Sentry not properly initialized, ignore
      }
    }
  }

  handleRetry = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  handleGoHome = (): void => {
    window.location.href = '/';
  };

  handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    const { t } = this.props;

    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI
      return (
        <div className={`flex items-center justify-center ${this.props.contained ? 'min-h-[50vh]' : 'min-h-screen'}`} style={{background: this.props.contained ? "transparent" : "var(--color-background-secondary)", paddingLeft: "var(--spacing-4)", paddingRight: "var(--spacing-4)"}}>
          <div className="w-full max-w-md" style={{borderRadius: "var(--border-radius-lg)", background: "var(--color-background-primary)", padding: "var(--spacing-8)", boxShadow: "var(--shadow-lg)"}}>
            {/* Error Icon */}
            <div className="mx-auto flex h-16 w-16 items-center justify-center" style={{marginBottom: "var(--spacing-6)", borderRadius: "var(--border-radius-full)", background: "var(--color-semantic-error-light)"}}>
              <AlertTriangle className="h-8 w-8 text-red-600" />
            </div>

            {/* Error Title */}
            <h1 className="text-center text-2xl font-bold" style={{marginBottom: "var(--spacing-2)", color: "var(--color-text-primary)"}}>
              {t('errors.boundary.title', 'Something went wrong')}
            </h1>

            {/* Error Description */}
            <p className="text-center" style={{marginBottom: "var(--spacing-6)", color: "var(--color-text-secondary)"}}>
              {t('errors.boundary.description', 'An unexpected error occurred. Please try again or return to the home page.')}
            </p>

            {/* Error Details (Development only) */}
            {import.meta.env.DEV && this.state.error && (
              <div style={{marginBottom: "var(--spacing-6)", borderRadius: "var(--border-radius-lg)", background: "var(--color-semantic-error-light)", padding: "var(--spacing-4)"}}>
                <p className="text-sm font-medium text-red-800" style={{marginBottom: "var(--spacing-2)"}}>
                  {t('errors.boundary.details', 'Error details (development mode only):')}
                </p>
                <pre className="max-h-32 overflow-auto text-xs text-red-700">
                  {this.state.error.message}
                </pre>
                {this.state.errorInfo && (
                  <details style={{marginTop: "var(--spacing-2)"}}>
                    <summary className="cursor-pointer text-xs text-red-600">
                      Component Stack
                    </summary>
                    <pre className="mt-1 max-h-32 overflow-auto text-xs" style={{color: "var(--color-semantic-error-DEFAULT)"}}>
                      {this.state.errorInfo.componentStack}
                    </pre>
                  </details>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col gap-3">
              <button
                onClick={this.handleReload}
                className="flex w-full items-center justify-center gap-2 py-3 font-medium transition-colors hover:bg-primary-700" style={{borderRadius: "var(--border-radius-lg)", background: "var(--color-primary-600)", paddingLeft: "var(--spacing-4)", paddingRight: "var(--spacing-4)", color: "var(--color-text-inverse)"}}
              >
                <RefreshCw className="h-4 w-4" />
                {t('errors.boundary.reload', 'Reload page')}
              </button>

              <button
                onClick={this.handleGoHome}
                className="flex w-full items-center justify-center gap-2 border py-3 font-medium transition-colors hover:bg-gray-50" style={{borderRadius: "var(--border-radius-lg)", borderColor: "var(--color-border-DEFAULT)", background: "var(--color-background-primary)", paddingLeft: "var(--spacing-4)", paddingRight: "var(--spacing-4)", color: "var(--color-text-secondary)"}}
              >
                <Home className="h-4 w-4" />
                {t('errors.boundary.goHome', 'Go to home')}
              </button>

              <button
                onClick={this.handleRetry}
                className="text-sm hover:text-gray-700 hover:underline" style={{color: "var(--color-text-tertiary)"}}
              >
                {t('errors.boundary.retry', 'Try again (without reload)')}
              </button>
            </div>

            {/* Support Info */}
            <p className="mt-6 text-center text-xs" style={{color: "var(--color-text-disabled)"}}>
              {t('errors.boundary.support', 'If the problem persists, please contact support.')}
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const ErrorBoundary = withTranslation()(ErrorBoundaryClass);
export default ErrorBoundary;
